import { useState } from 'react';
import { useI18n } from '@/i18n/I18nProvider';

const languages = [
  { name: 'French', code: 'fr' },
  { name: 'Spanish', code: 'es' },
  { name: 'German', code: 'de' },
  { name: 'Portuguese', code: 'pt' },
  { name: 'Russian', code: 'ru' },
];

const LanguageModal = () => {
  const { t, locale, setLocale } = useI18n();
  const [error, setError] = useState('');
  const [isTranslated, setIsTranslated] = useState(false);

  const changeCommonLanguage = (language: 'en' | 'ar') => {
    setLocale(language);
    if (isTranslated) {
      window.location.reload();
    }
  };

  const translatePage = async (targetLanguage: string) => {
    setError('');

    if (!('Translator' in window)) {
      setError('Translation feature is not supported in this browser.');
      return;
    }

    try {
      // @ts-ignore - experimental API
      const translator = await window.Translator.create({
        sourceLanguage: locale,
        targetLanguage,
      });

      const elements = document.querySelectorAll('body *:not(script):not(style)');

      for (const element of elements) {
        if (element.childNodes.length === 1 && element.firstChild?.nodeType === 3) {
          const text = element.textContent?.trim();

          if (text) {
            // @ts-ignore
            element.textContent = await translator.translate(text);
          }
        }
      }
      setIsTranslated(true);
    } catch {
      setError('Unable to translate this page.');
    }
  };

  return (
    <div id="modal-lang" tabIndex={-1} aria-hidden="true" className="aegov-modal hidden z-[60] shadow-xl" role="dialog">
      <div className="relative sm:w-full sm:max-w-sm max-h-full">
        <div className="aegov-modal-wrapper py-4 md:py-5 xl:py-8 px-4 xl:px-6">
          <button type="button" className="aegov-modal-close top-2 end-2" data-modal-hide="modal-lang">
            <svg
              aria-hidden="true"
              className="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              ></path>
            </svg>
            <span className="sr-only">{t.common.close}</span>
          </button>
          <div>
            <div className="lang-header">
              <button
                type="button"
                className={`lang-primary ${locale === 'en' ? 'active' : ''}`}
                onClick={() => changeCommonLanguage('en')}
                data-modal-hide="modal-lang"
              >
                {t.common.english}
              </button>
              <span className="lang-primary-divider"></span>
              <button
                type="button"
                className={`lang-primary font-notokufi ${locale === 'ar' ? 'active' : ''}`}
                onClick={() => changeCommonLanguage('ar')}
                data-modal-hide="modal-lang"
              >
                {t.common.arabic}
              </button>
            </div>
            <div className="lang-other px-3 md:px-4 xl:px-5">
              <div className="px-3 md:px-4 xl:px-5">
                <p className="lang-other-title">{t.common.otherLanguages}</p>
                <ul className="divide-y divide-aeblack-100">
                  {languages.map((language) => (
                    <li key={language.code}>
                      <a className="lang-other-link cursor-pointer" onClick={() => translatePage(language.code)}>
                        {language.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {error && <p className="mt-4 text-red-600">{error}</p>}

            <div className="mt-6 md:mt-8 lg:mt-10 xl:mt-12">
              <p className="lang-bottom-text">{t.common.translationDisclaimer}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguageModal;
